import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page } from "react-pdf";

import { ProgressSpinner } from "primereact/progressspinner";

import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useDebounce } from "../../../../../utils/hooks/debounceHook";
import { translationKeys } from "../../../../models/app-translation-keys";
import { useSharedDialogState } from "../../../Shared/Components/Dialogs/SharedDialog";
import { ITermsOfUseDialogProps } from "../TermsOfUseDialog";
import { ITermsOfUseDialogState } from "../termsOfUseDialogTypes.ts";

import "./TermsOfUseDialogContent.scss";

const termsOfUseAndDataPrivacyPolicyKeys = translationKeys.termsOfUseAndDataPrivacyPolicy;

interface ITermsOfUseDialogContentProps {
  base64PdfDocument: ITermsOfUseDialogProps["base64PdfDocument"];
  isLoading: ITermsOfUseDialogProps["isLoading"];
}

export const TermsOfUseDialogContent: FunctionComponent<ITermsOfUseDialogContentProps> = (
  props: ITermsOfUseDialogContentProps,
) => {
  const [numPages, setNumPages] = useState<number>(0);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const resizeDebounceMs = 120;

  const { t } = useTranslation();
  const { setSharedDialogState } = useSharedDialogState<ITermsOfUseDialogState>();

  const documentUrl = (base64PdfDocument: string) => {
    return `data:application/pdf;base64,${base64PdfDocument}`;
  };

  const updateScrolledToBottom = (isScrolledToBottom: boolean) => (previousState: ITermsOfUseDialogState) => {
    if (previousState.isScrolledToBottom === isScrolledToBottom) {
      return previousState;
    }

    return {
      ...previousState,
      isScrolledToBottom,
    };
  };

  const handleScroll = () => {
    const container: HTMLDivElement | null = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const isBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 1;
    if (!isBottom) {
      return;
    }

    // once the user reached the bottom, keep that state stable
    setSharedDialogState(updateScrolledToBottom(true));
    removeScrollEventListener(container);
  };

  const checkIfScrollable = (): void => {
    const container: HTMLDivElement | null = scrollContainerRef.current;
    if (!container) {
      return;
    }

    // never unlock before the PDF has actually rendered.
    if (numPages <= 0) {
      setSharedDialogState(updateScrolledToBottom(false));
      return;
    }

    // checks whether the container actually has a scrollbar
    const hasScroll = Math.ceil(container.scrollHeight) > Math.floor(container.clientHeight) + 1;
    if (hasScroll) {
      addScrollEventListener(container); // if there is a scrollbar, attach scroll handler
      handleScroll(); // and check if the content is already scrolled to bottom
    } else {
      removeScrollEventListener(container); // if there is no scrollbar, remove scroll handler
      setSharedDialogState(updateScrolledToBottom(true)); // and consider the content is already scrolled to bottom
    }
  };

  const addScrollEventListener = (container: HTMLDivElement) => {
    container.addEventListener("scroll", handleScroll);
  };

  const removeScrollEventListener = (container: HTMLDivElement) => {
    container.removeEventListener("scroll", handleScroll);
  };

  const onWindowResize = useDebounce(() => checkIfScrollable(), resizeDebounceMs);

  // resets state whenever a new document is shown
  useEffect(() => {
    setSharedDialogState(previousState => {
      if (!previousState.isChecked && !previousState.isScrolledToBottom) {
        return previousState;
      }

      return {
        ...previousState,
        isChecked: false,
        isScrolledToBottom: false,
      };
    });

    setNumPages(0);
  }, [props.base64PdfDocument]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    // calls for the scroll measurements when the layout is stable
    requestAnimationFrame(() => {
      window.addEventListener("resize", onWindowResize);
      onWindowResize(); // initial check if the container is scrollable
    });

    return () => {
      window.removeEventListener("resize", onWindowResize);
      removeScrollEventListener(container);
    };
  }, [scrollContainerRef?.current, onWindowResize]);

  useEffect(() => {
    if (numPages <= 0) {
      return;
    }

    // waits a frame so rendered pages affect scrollHeight before measuring
    requestAnimationFrame(() => checkIfScrollable());
  }, [numPages]);

  return props.isLoading ? (
    <div className="terms-of-use-content-loading">
      <ProgressSpinner />
    </div>
  ) : props.base64PdfDocument ? (
    <div className="terms-of-use-content" ref={scrollContainerRef}>
      <Document
        file={documentUrl(props.base64PdfDocument)}
        onLoadSuccess={({ numPages }: any) => {
          setNumPages(numPages);
          requestAnimationFrame(() => checkIfScrollable());
        }}
      >
        {Array.from({ length: numPages }, (_, index) => (
          <Page key={index} pageNumber={index + 1} renderTextLayer={true} renderAnnotationLayer={true} />
        ))}
      </Document>
    </div>
  ) : (
    <div className="terms-of-use-content-error">
      <FontAwesomeIcon icon={faTriangleExclamation} size="2x" color="#ff0000" />

      <span style={{ color: "var(--red-400)" }}>{t(termsOfUseAndDataPrivacyPolicyKeys.notAvailable)}</span>
    </div>
  );
};
